/**
 * Order again — lists this device's remembered lunches and hands one back to the funnel.
 *
 * Reads assets/order-memory.js. Everything here is presentation: the decision about *why* the
 * history lives in the browser rather than on the customer record is documented there.
 */
class OrderAgain extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-order-again-list]');
    this.empty = this.querySelector('[data-order-again-empty]');
    this.loading = this.querySelector('[data-order-again-loading]');
    this.row = this.querySelector('[data-order-again-row]');
    this.builderUrl = this.dataset.builderUrl || '/';

    this.render();
  }

  render() {
    const orders = window.OrderMemory?.list() || [];

    this.loading?.setAttribute('hidden', '');
    this.list.replaceChildren();
    this.list.toggleAttribute('hidden', orders.length === 0);
    this.empty?.toggleAttribute('hidden', orders.length > 0);

    orders.forEach((order) => this.list.appendChild(this.buildRow(order)));
  }

  buildRow(order) {
    const node = this.row.content.firstElementChild.cloneNode(true);

    /*
     * textContent throughout, never innerHTML: the student's name is free text a parent typed,
     * and it is being read back out of storage where anything could have been put.
     */
    node.querySelector('[data-row-title]').textContent = order.product?.label || 'Lunch';
    node.querySelector('[data-row-detail]').textContent = OrderAgain.detail(order);
    node.querySelector('[data-row-meta]').textContent = OrderAgain.meta(order);

    node.querySelector('[data-row-repeat]').addEventListener('click', () => this.repeat(order));
    node.querySelector('[data-row-forget]').addEventListener('click', () => {
      window.OrderMemory?.forget(order.id);
      this.render();
    });

    return node;
  }

  /** What is in the lunch: the extras, the bread, and anything taken off. */
  static detail(order) {
    const parts = (order.addons || []).map((addon) => addon.label).filter(Boolean);
    const bread = order.build?.bread;
    const removed = order.build?.removed || [];

    const lines = [];
    if (parts.length) lines.push(`with ${OrderAgain.listFormat(parts)}`);
    if (bread) lines.push(`on ${bread}`);
    if (removed.length) lines.push(`no ${OrderAgain.listFormat(removed).toLowerCase()}`);
    return lines.join(' · ');
  }

  /** Who it was for and how much of it — the numbers a parent checks before repeating. */
  static meta(order) {
    const bits = [];
    if (order.student) bits.push(`For ${order.student}`);
    if (order.quantity > 1) bits.push(`${order.quantity} lunches per day`);
    if (order.days) bits.push(`${order.days} ${order.days === 1 ? 'day' : 'days'} last time`);

    const when = OrderAgain.when(order.savedAt);
    if (when) bits.push(when);
    return bits.join(' · ');
  }

  static when(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return `Ordered ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }

  static listFormat(items) {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  /**
   * Hands the order to the funnel and goes there. If storage refuses the handover — private
   * browsing, a full quota — the customer is sent to the builder anyway rather than left on a
   * button that does nothing; they build it themselves, which is the state they were in before
   * this page existed.
   */
  repeat(order) {
    window.OrderMemory?.putSeed(order);
    window.location = this.builderUrl;
  }
}

if (!customElements.get('order-again')) {
  customElements.define('order-again', OrderAgain);
}
